import { cn } from "@/lib/utils";
import { Check, ChevronDown, Clock, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError, fetchMoreGames, fetchSummoner, refreshSummoner } from "../api";
import type { MatchHistory, SummonerProfile } from "../types";
import { getSummonerIconUrl } from "../utils/ddragon";
import { parseRiotId, REGIONS, rememberSearch, summonerPath } from "../utils/regions";
import { mmss, useDeadline, useSecondsUntil } from "../utils/time";
import { REGION_NAMES } from "./SearchBar";
import { LiveTab } from "./summoner/LiveTab";
import { ChampionsCard, HighlightsCard, LpHistoryCard, MatchRow, MatchRowSkeleton, RanksCard, RecentForm, matchCardClass } from "./summoner/parts";
import { ScoreboardPanel } from "./summoner/Scoreboard";
import { button, Skeleton } from "./ui/primitives";

const QUEUES: { key: string; label: string }[] = [
	{ key: "", label: "All" },
	{ key: "solo", label: "Solo/Duo" },
	{ key: "flex", label: "Flex" },
	{ key: "normal", label: "Normal" },
	{ key: "aram", label: "ARAM" }
];

const POLL_MS = 3000;
const MAX_POLLS = 15; // ~45 s: background games normally land well before that

function timeAgo(ms: number | null): string | null {
	if (!ms) return null;
	const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
	if (s < 60) return "just now";
	const m = Math.floor(s / 60);
	return m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ago`;
}

// ─── Page-level states ───────────────────────────────────────────────────────

function ProfileSkeleton() {
	return (
		<section aria-busy="true" aria-label="Loading summoner" className="pt-6">
			<div className="flex items-center gap-4 pb-5">
				<Skeleton className="h-14 w-14 rounded-[14px] sm:h-[72px] sm:w-[72px]" />
				<div className="flex flex-1 flex-col gap-2.5">
					<Skeleton className="h-6 w-[220px] max-w-[70%]" />
					<Skeleton className="h-3 w-[150px] max-w-[50%]" />
				</div>
			</div>
			<div className="mb-5 h-[43px] border-b border-line" />
			<div className="flex flex-col gap-5 lg:flex-row lg:items-start">
				<div className="flex flex-col gap-3 lg:w-[340px] lg:shrink-0">
					<Skeleton className="h-[132px] rounded-[10px]" />
					<Skeleton className="hidden h-[220px] rounded-[10px] md:block" />
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-1.5">
					<Skeleton className="mb-1.5 h-9 w-[300px] max-w-full rounded-lg" />
					{Array.from({ length: 6 }, (_, i) => (
						<MatchRowSkeleton key={i} />
					))}
				</div>
			</div>
		</section>
	);
}

function NotFoundState({ region, nameTag }: { region: string; nameTag: string }) {
	const id = parseRiotId(nameTag);
	const shown = id ? `${id.name}#${id.tag}` : nameTag;
	return (
		<section className="mx-auto max-w-[560px] pt-[72px] pb-6">
			<div className="mb-3 font-mono text-xs text-ink-3">No match · {region.toUpperCase()}</div>
			<h1 className="m-0 mb-2.5 text-[26px] font-extrabold leading-[1.1] text-balance [font-stretch:112%] sm:text-[30px]">
				We couldn&apos;t find {shown} in {REGION_NAMES[region] ?? region.toUpperCase()}.
			</h1>
			<p className="m-0 mb-[22px] text-[15px] leading-relaxed text-ink-2 text-pretty">
				Check the tag after the #. It&apos;s case-insensitive but has to match exactly. The account may also play on another server.
			</p>
			{id && (
				<>
					<div className="mb-2 text-[11px] font-semibold uppercase tracking-[.09em] text-ink-3">Try another region</div>
					<div className="mb-[26px] flex flex-wrap gap-2">
						{REGIONS.filter(r => r.slug !== region)
							.slice(0, 6)
							.map(r => (
								<Link key={r.slug} to={summonerPath(r.slug, id.name, id.tag)} className="inline-flex h-10 items-center rounded-lg border border-line-2 bg-surface px-3.5 text-[13px] font-bold tracking-[.05em] hover:border-signal hover:text-ink">
									{r.label}
								</Link>
							))}
					</div>
				</>
			)}
			<Link to="/" className={button.primary + " hover:text-on-signal"}>
				New search
			</Link>
		</section>
	);
}

function BusyState({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
	const until = useDeadline(error.retryAfterSeconds ?? 30, error);
	const left = useSecondsUntil(until);
	const total = error.retryAfterSeconds ?? 30;
	const fired = useRef(false);
	useEffect(() => {
		if (left === 0 && until && !fired.current) {
			fired.current = true;
			onRetry();
		}
	}, [left, until, onRetry]);
	const C = 150.8; // 2πr, r = 24
	return (
		<section aria-live="polite" className="mx-auto max-w-[560px] pt-[72px] pb-6">
			<div className="mb-[18px] flex items-center gap-3.5">
				<div className="relative h-14 w-14">
					<svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
						<circle cx="28" cy="28" r="24" fill="none" stroke="var(--color-line)" strokeWidth="4" />
						<circle cx="28" cy="28" r="24" fill="none" stroke="var(--color-signal)" strokeWidth="4" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - left / total)} transform="rotate(-90 28 28)" className="transition-[stroke-dashoffset] duration-1000 ease-linear" />
					</svg>
					<div className="absolute inset-0 flex items-center justify-center font-mono text-sm font-semibold" role="timer">
						{mmss(left)}
					</div>
				</div>
				<div className="font-mono text-xs text-ink-3">HTTP {error.status} · rate limited</div>
			</div>
			<h1 className="m-0 mb-2.5 text-[30px] font-extrabold leading-[1.1] [font-stretch:112%]">Riot&apos;s servers are busy.</h1>
			<p className="m-0 mb-6 text-[15px] leading-relaxed text-ink-2 text-pretty">
				{error.message}. We&apos;ll try again automatically in {mmss(left)}, or you can retry now.
			</p>
			<div className="flex flex-wrap gap-2.5">
				<button type="button" onClick={onRetry} className={button.primary}>
					Retry now
				</button>
				<Link to="/" className={button.ghost + " hover:text-ink"}>
					New search
				</Link>
			</div>
		</section>
	);
}

function GenericError({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
	return (
		<section className="mx-auto max-w-[560px] pt-[72px] pb-6">
			<h1 className="m-0 mb-2.5 text-[28px] font-extrabold [font-stretch:112%]">Something went wrong.</h1>
			<p className="m-0 mb-6 text-[15px] leading-relaxed text-ink-2">{error.message}</p>
			<div className="flex flex-wrap gap-2.5">
				<button type="button" onClick={onRetry} className={button.primary}>
					Try again
				</button>
				<Link to="/" className={button.ghost + " hover:text-ink"}>
					New search
				</Link>
			</div>
		</section>
	);
}

// ─── Banners ─────────────────────────────────────────────────────────────────

function StaleBanner({ retryAfterSeconds, updatedAt, onUpdate }: { retryAfterSeconds?: number; updatedAt: number | null; onUpdate: () => void }) {
	const until = useDeadline(retryAfterSeconds, updatedAt);
	const left = useSecondsUntil(until);
	const ago = timeAgo(updatedAt);
	return (
		<div role="status" className="mb-3.5 flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-[10px] border border-line-2 bg-surface px-3.5 py-2.5 text-[13px] text-ink-2">
			<Clock aria-hidden="true" className="h-4 w-4 text-ink" strokeWidth={2.2} />
			<span className="flex-[1_1_240px]">
				<span className="font-semibold text-ink">Showing saved data{ago ? ` from ${ago}` : ""}.</span> Riot is busy
				{left > 0 ? (
					<>
						, fresh data in about <span className="font-mono text-ink">{mmss(left)}</span>.
					</>
				) : (
					", try Update again now."
				)}
			</span>
			<button type="button" onClick={onUpdate} className="h-8 cursor-pointer rounded-md border border-line-2 px-3 text-xs font-semibold hover:bg-raised">
				Try Update
			</button>
		</div>
	);
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SummonerPage() {
	const { region = "", nameTag = "" } = useParams<{ region: string; nameTag: string }>();
	const [search, setSearch] = useSearchParams();
	const tab = search.get("tab") === "live" ? "live" : "overview";

	const [queue, setQueue] = useState("");
	const [profile, setProfile] = useState<SummonerProfile | null>(null);
	const [games, setGames] = useState<MatchHistory[]>([]);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<ApiError | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [justUpdated, setJustUpdated] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
	const [open, setOpen] = useState<string | null>(null);
	const [everOpened, setEverOpened] = useState<Set<string>>(() => new Set());
	const pages = useRef(1); // once the user loaded more, background polling must not replace their list

	const cooldownLeft = useSecondsUntil(cooldownUntil);

	const apply = useCallback((p: SummonerProfile) => {
		setProfile(p);
		setGames(p.games);
		setHasMore(p.hasMore);
		pages.current = 1;
	}, []);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			apply(await fetchSummoner(region, nameTag, queue || undefined));
		} catch (e) {
			setError(e instanceof ApiError ? e : new ApiError("Could not load this summoner", 0));
		} finally {
			setLoading(false);
		}
	}, [region, nameTag, queue, apply]);

	// a different summoner starts from a clean page
	useEffect(() => {
		setProfile(null);
		setQueue("");
		setEverOpened(new Set());
		setCooldownUntil(null);
		setNotice(null);
	}, [region, nameTag]);

	// (re)load whenever the summoner or the queue filter changes
	useEffect(() => {
		setOpen(null);
		load();
	}, [load]);

	// Remember successful searches for the home page
	useEffect(() => {
		if (profile) rememberSearch({ name: profile.summoner.name, tag: profile.summoner.tagLine, region: profile.summoner.region, profileIconId: profile.summoner.profileIconId });
	}, [profile?.summoner.name, profile?.summoner.tagLine, profile?.summoner.region]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		document.title = profile ? `${profile.summoner.name}#${profile.summoner.tagLine} · RiftWatch` : "RiftWatch";
	}, [profile]);

	// While the server is still fetching older games in the background, poll (cheap: no Riot calls) until they land.
	const stillRefreshing = profile?.status.refreshing ?? false;
	useEffect(() => {
		if (!stillRefreshing) return;
		let polls = 0;
		let cancelled = false;
		const timer = setInterval(async () => {
			if (++polls > MAX_POLLS) return clearInterval(timer);
			try {
				const p = await fetchSummoner(region, nameTag, queue || undefined);
				if (cancelled) return;
				if (pages.current === 1) apply(p);
				else setProfile(prev => (prev ? { ...prev, status: p.status } : p));
				if (!p.status.refreshing) clearInterval(timer);
			} catch {
				/* keep what we have */
			}
		}, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [stillRefreshing, region, nameTag, queue, apply]);

	const loadMore = async () => {
		const last = games[games.length - 1];
		if (!last || loadingMore) return;
		setLoadingMore(true);
		try {
			const page = await fetchMoreGames(region, nameTag, new Date(last.timestamp).getTime(), queue || undefined);
			pages.current++;
			setGames(prev => [...prev, ...page.games.filter(g => !prev.some(x => x.matchId === g.matchId))]);
			setHasMore(page.hasMore);
			setProfile(prev => (prev ? { ...prev, status: page.status } : prev));
			if (page.status.stale) setNotice(`Riot is busy, older games could not be loaded. Try again in ${page.status.retryAfterSeconds ?? 30}s.`);
		} catch (e) {
			setNotice(e instanceof ApiError ? e.message : "Could not load more games");
		} finally {
			setLoadingMore(false);
		}
	};

	const update = async () => {
		if (refreshing) return;
		if (cooldownLeft > 0) return; // the button already says when; the status line explains why
		setRefreshing(true);
		setNotice(null);
		try {
			apply(await refreshSummoner(region, nameTag, queue || undefined));
			setJustUpdated(true);
			setTimeout(() => setJustUpdated(false), 4000);
		} catch (e) {
			if (e instanceof ApiError && e.status === 429 && e.cooldownRemaining) setCooldownUntil(Date.now() + e.cooldownRemaining * 1000);
			else {
				setNotice(e instanceof ApiError ? e.message : "Could not update");
				setTimeout(() => setNotice(null), 6000);
			}
		} finally {
			setRefreshing(false);
		}
	};

	const toggle = (id: string) => {
		setOpen(o => (o === id ? null : id));
		setEverOpened(s => (s.has(id) ? s : new Set(s).add(id)));
	};

	if (loading && !profile) return <ProfileSkeleton />;
	if (error && !profile) {
		if (error.status === 404) return <NotFoundState region={region} nameTag={nameTag} />;
		if (error.status === 429 || error.status === 503) return <BusyState error={error} onRetry={load} />;
		return <GenericError error={error} onRetry={load} />;
	}
	if (!profile) return null;

	const { summoner, ranks, lpHistory, champions, highlights, status } = profile;
	const me = `${summoner.name}#${summoner.tagLine}`.toLowerCase();
	const updated = justUpdated ? "just now" : timeAgo(status.updatedAt);
	const queueName = QUEUES.find(q => q.key === queue)?.label ?? "";

	return (
		<div>
			{/* Header */}
			<section className="flex flex-wrap items-center gap-x-6 gap-y-4 pt-[18px] pb-3.5 sm:pt-7 sm:pb-5">
				<div className="flex min-w-0 flex-[1_1_280px] items-center gap-3.5">
					<div className="relative shrink-0">
						<img src={getSummonerIconUrl(summoner.profileIconId)} alt="" className="block h-14 w-14 rounded-[14px] shadow-[0_0_0_1px_var(--color-line-2)] sm:h-[72px] sm:w-[72px]" />
						{summoner.summonerLevel != null && (
							<span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-line-2 bg-page px-[7px] py-px font-mono text-[11px] font-bold" aria-label={`Level ${summoner.summonerLevel}`}>
								{summoner.summonerLevel}
							</span>
						)}
					</div>
					<div className="min-w-0">
						<h1 className="m-0 truncate text-[22px] font-extrabold leading-[1.1] [font-stretch:110%] sm:text-[30px]">
							{summoner.name}
							<span className="font-medium text-ink-3 [font-stretch:100%]">#{summoner.tagLine}</span>
						</h1>
						<div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[13px] text-ink-3">
							<span className="rounded bg-raised px-1.5 py-0.5 text-[11px] font-bold tracking-[.06em] text-ink">{summoner.region.toUpperCase()}</span>
							{updated && <span>Updated {updated}</span>}
						</div>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-4">
					<RecentForm games={games.slice(0, highlights.games || 20)} />
					<button
						type="button"
						onClick={update}
						disabled={refreshing || cooldownLeft > 0}
						aria-describedby="update-status"
						title="Fetch the latest data from Riot"
						className={cn(
							"flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-line-2 bg-surface px-3.5 text-[13px] font-semibold transition-colors hover:border-line-3 hover:bg-raised disabled:cursor-default",
							cooldownLeft > 0 ? "text-ink-3" : "text-ink"
						)}
					>
						<RefreshCw aria-hidden="true" className={cn("h-[15px] w-[15px]", refreshing && "animate-spin")} strokeWidth={2.3} />
						<span>{refreshing ? "Updating…" : cooldownLeft > 0 ? `Update in ${mmss(cooldownLeft)}` : "Update"}</span>
					</button>
				</div>
			</section>

			<div id="update-status" aria-live="polite">
				{status.stale && <StaleBanner retryAfterSeconds={status.retryAfterSeconds} updatedAt={status.updatedAt} onUpdate={update} />}
				{cooldownLeft > 0 && (
					<div role="status" className="mb-3.5 flex items-center gap-2.5 rounded-[10px] border border-line-2 bg-surface px-3.5 py-2.5 text-[13px] text-ink-2 animate-reveal">
						<Check aria-hidden="true" className="h-4 w-4 text-signal" strokeWidth={2.4} />
						<span>
							<span className="font-semibold text-ink">Already up to date.</span> You can update again in <span className="font-mono text-ink">{mmss(cooldownLeft)}</span>.
						</span>
					</div>
				)}
				{notice && <div className="mb-3.5 rounded-[10px] border border-line-2 bg-surface px-3.5 py-2.5 text-[13px] text-ink-2">{notice}</div>}
			</div>

			{/* Tabs */}
			<div role="tablist" aria-label="Profile sections" className="mb-[18px] flex gap-1 border-b border-line">
				{(["overview", "live"] as const).map(t => (
					<button
						key={t}
						type="button"
						role="tab"
						id={`tab-${t}`}
						aria-selected={tab === t}
						aria-controls={`panel-${t}`}
						onClick={() => setSearch(t === "live" ? { tab: "live" } : {}, { replace: true })}
						onKeyDown={e => {
							if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
								const next = t === "overview" ? "live" : "overview";
								setSearch(next === "live" ? { tab: "live" } : {}, { replace: true });
								document.getElementById(`tab-${next}`)?.focus();
							}
						}}
						tabIndex={tab === t ? 0 : -1}
						className={cn("h-[42px] cursor-pointer px-3.5 text-sm font-bold transition-colors hover:text-ink", tab === t ? "text-ink shadow-[inset_0_-2px_0_var(--color-signal)]" : "text-ink-3")}
					>
						{t === "overview" ? "Overview" : "Live game"}
					</button>
				))}
			</div>

			{tab === "live" ? (
				<div role="tabpanel" id="panel-live" aria-labelledby="tab-live">
					<LiveTab region={region} nameTag={nameTag} />
				</div>
			) : (
				<div role="tabpanel" id="panel-overview" aria-labelledby="tab-overview" className="flex flex-col gap-5 lg:flex-row lg:items-start">
					<aside className="grid min-w-0 gap-3 md:grid-cols-2 md:items-start lg:flex lg:w-[340px] lg:shrink-0 lg:flex-col">
						<RanksCard solo={ranks.solo} flex={ranks.flex} premade={ranks.premade} />
						<LpHistoryCard history={lpHistory} />
						<HighlightsCard highlights={highlights} />
						<ChampionsCard champions={champions} games={highlights.games} />
					</aside>

					<div className="min-w-0 flex-1">
						<div className="mb-2.5 flex flex-wrap items-center justify-between gap-2.5">
							<div role="group" aria-label="Queue filter" className="flex max-w-full gap-0.5 overflow-x-auto rounded-[9px] border border-line bg-surface p-[3px]">
								{QUEUES.map(q => (
									<button
										key={q.key}
										type="button"
										aria-pressed={queue === q.key}
										onClick={() => setQueue(q.key)}
										className={cn("h-8 cursor-pointer whitespace-nowrap rounded-md px-3 text-[13px] font-semibold transition-colors hover:text-ink", queue === q.key ? "bg-pressed text-ink" : "text-ink-3")}
									>
										{q.label}
									</button>
								))}
							</div>
							{status.refreshing && !loading && (
								<div role="status" className="flex items-center gap-2 text-xs text-ink-2">
									<span aria-hidden="true" className="relative h-[3px] w-[60px] overflow-hidden rounded-sm bg-line">
										<span className="absolute inset-y-0 left-0 w-[35%] rounded-sm bg-signal animate-slide" />
									</span>
									Fetching older games
								</div>
							)}
						</div>

						{loading ? (
							<div aria-busy="true" aria-label="Loading games" className="flex flex-col gap-1.5">
								{Array.from({ length: 5 }, (_, i) => (
									<MatchRowSkeleton key={i} />
								))}
							</div>
						) : games.length === 0 ? (
							status.refreshing ? (
								<div aria-busy="true" aria-label="Fetching games" className="flex flex-col gap-1.5">
									{Array.from({ length: 3 }, (_, i) => (
										<MatchRowSkeleton key={i} />
									))}
								</div>
							) : (
								<div className="rounded-[10px] border border-dashed border-line-2 bg-surface px-5 py-12 text-center">
									<div className="mb-1.5 text-base font-bold">No {queue ? `${queueName} ` : ""}games in recent history</div>
									<div className="mb-4 text-sm text-ink-2">Older games may still be loading, or this queue wasn&apos;t played lately.</div>
									{queue && (
										<button type="button" onClick={() => setQueue("")} className={button.secondary + " hover:border-signal"}>
											Show all queues
										</button>
									)}
								</div>
							)
						) : (
							<>
								<ol aria-label="Recent games" className="m-0 flex list-none flex-col gap-1.5 p-0">
									{games.map(g => {
										const isOpen = open === g.matchId;
										const panelId = `sb-${g.matchId}`;
										return (
											<li key={g.matchId} className={matchCardClass(g, isOpen)}>
												<MatchRow match={g} expanded={isOpen} onToggle={() => toggle(g.matchId)} controls={panelId} />
												<div id={panelId} className={cn("grid transition-[grid-template-rows] duration-[250ms] ease-out", isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")} inert={!isOpen}>
													<div className="min-h-0 overflow-hidden">
														{everOpened.has(g.matchId) && (
															<div className="border-t border-line bg-sunken px-2 py-2.5 sm:p-3">
																<ScoreboardPanel matchId={g.matchId} region={summoner.region} me={me} />
															</div>
														)}
													</div>
												</div>
											</li>
										);
									})}
									{(loadingMore || status.refreshing) && <MatchRowSkeleton />}
								</ol>
								{hasMore ? (
									<button
										type="button"
										onClick={loadMore}
										disabled={loadingMore}
										className="mt-2.5 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-[9px] border border-line bg-surface text-[13px] font-semibold text-ink-2 transition-colors hover:border-line-3 hover:text-ink disabled:cursor-default"
									>
										{!loadingMore && <ChevronDown aria-hidden="true" className="h-4 w-4" />}
										{loadingMore ? "Loading older games…" : "Show more games"}
									</button>
								) : (
									!status.refreshing && <div className="pt-4 text-center text-xs text-ink-3">That&apos;s every game we have for this filter.</div>
								)}
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
